import { useState, useEffect, useMemo } from 'react';
import { Newspaper, Calendar, User, ArrowRight, ExternalLink, AlertTriangle, Search } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { SEOPage } from '@/components/SEO';
import api from '@/lib/api';

const HERO_BG = "https://customer-assets.emergentagent.com/job_ae021ef0-7027-4ae5-b97f-cd7f1827f457/artifacts/z1lv2e2v_P2341000.JPG";
const NEWS_FEED_URL = "https://paisley.org.uk/api/posts";
const NEWS_DETAIL_BASE = "https://paisley.org.uk/news/";
const HUB_URL = "https://paisley.org.uk/news";

const EXCLUDED_CATEGORIES = new Set(['announcements']);

const formatDate = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

const NewsListPage = () => {
  const [localPosts, setLocalPosts] = useState([]);
  const [externalPosts, setExternalPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errored, setErrored] = useState(false);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('all');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Local business news (paisleyscotland.com) — our own posts
      const localP = api.get('/news')
        .then(r => (r.data || []).filter(p => p.is_published !== false).map(p => ({
          id: p.id,
          title: p.title,
          slug: p.slug,
          excerpt: p.excerpt || (p.meta_description || ''),
          featured_image: p.cover_image,
          category: p.tags?.[0] || 'business',
          author: p.author || 'Paisley Business',
          created_at: p.created_at,
          origin: 'local',
        })))
        .catch(() => []);

      // Paisley.org.uk news — supplementary until local grows
      const externalP = fetch(NEWS_FEED_URL, { cache: 'no-store' })
        .then(r => r.ok ? r.json() : [])
        .then(data => (Array.isArray(data) ? data : [])
          .filter(p => p.published !== false)
          .filter(p => !EXCLUDED_CATEGORIES.has((p.category || '').toLowerCase()))
          .map(p => ({ ...p, origin: 'paisley.org.uk' })))
        .catch(() => []);

      const [local, external] = await Promise.all([localP, externalP]);
      if (cancelled) return;
      setLocalPosts(local);
      setExternalPosts(external);
      setErrored(local.length === 0 && external.length === 0);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);

  const posts = useMemo(() => [...localPosts, ...externalPosts], [localPosts, externalPosts]);

  const categories = useMemo(() => {
    const list = [];
    for (const p of posts) {
      const c = (p.category || '').trim().toLowerCase();
      if (c && !list.includes(c)) list.push(c);
    }
    return ['all', ...list];
  }, [posts]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return posts.filter(p => {
      const matchesSearch = !q ||
        (p.title || '').toLowerCase().includes(q) ||
        (p.excerpt || '').toLowerCase().includes(q);
      const matchesCategory = category === 'all' || (p.category || '').toLowerCase() === category;
      return matchesSearch && matchesCategory;
    });
  }, [posts, search, category]);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="news-list-page">
      <SEOPage.News />
      <Navbar />

      <section
        className="relative pt-32 pb-20"
        style={{ backgroundImage: `url(${HERO_BG})`, backgroundSize: 'cover', backgroundPosition: 'center' }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-slate-900/90 to-slate-900/95" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Badge className="mb-4 bg-sky-500/20 text-sky-400 border-sky-500/30">
            <Newspaper className="h-3 w-3 mr-1" /> News & Stories
          </Badge>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 tracking-tight">
            Paisley News
          </h1>
          <p className="text-slate-300 text-lg max-w-2xl mx-auto">
            Live news, history and features from Paisley &amp; Renfrewshire — mirrored from Paisley.org.uk.
          </p>
        </div>
      </section>

      {/* Filter bar */}
      <section className="py-8 bg-white border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Search news…"
              className="pl-9"
              value={search}
              onChange={e => setSearch(e.target.value)}
              data-testid="news-search-input"
            />
          </div>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="md:w-56" data-testid="news-category-filter">
              <SelectValue placeholder="All categories" />
            </SelectTrigger>
            <SelectContent>
              {categories.map(c => (
                <SelectItem key={c} value={c}>
                  {c === 'all' ? 'All categories' : c.charAt(0).toUpperCase() + c.slice(1).replace('-', ' ')}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </section>

      {/* Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1,2,3,4,5,6].map(i => (
                <Card key={i} className="animate-pulse overflow-hidden">
                  <div className="h-48 bg-slate-200" />
                  <CardContent className="p-5">
                    <div className="h-5 bg-slate-200 rounded w-3/4 mb-3" />
                    <div className="h-4 bg-slate-100 rounded w-full mb-2" />
                    <div className="h-4 bg-slate-100 rounded w-2/3" />
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : errored ? (
            <Card className="max-w-2xl mx-auto" data-testid="news-fallback">
              <CardContent className="p-10 text-center">
                <AlertTriangle className="h-10 w-10 text-amber-400 mx-auto mb-4" />
                <h3 className="font-heading text-xl font-semibold text-slate-900 mb-2">
                  News temporarily unavailable
                </h3>
                <p className="text-slate-600 mb-6">
                  Read the full archive at{' '}
                  <a href={HUB_URL} className="text-sky-600 hover:text-sky-700 font-medium underline">
                    paisley.org.uk/news
                  </a>.
                </p>
                <a href={HUB_URL}>
                  <Button className="bg-sky-500 hover:bg-sky-600 text-white">
                    Go to Paisley.org.uk News <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </a>
              </CardContent>
            </Card>
          ) : filtered.length === 0 ? (
            <div className="text-center py-16">
              <Newspaper className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-900 mb-2">
                No stories match your filters
              </h3>
              <p className="text-slate-500">Try clearing the search or category filter.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((post, i) => {
                const date = formatDate(post.created_at);
                const isLocal = post.origin === 'local';
                const href = isLocal ? `/news/${post.slug}` : `${NEWS_DETAIL_BASE}${post.slug}`;
                const CardWrapper = ({ children }) => isLocal
                  ? <Link to={href} className="group block" data-testid={`news-card-${i}`}>{children}</Link>
                  : <a href={href} target="_blank" rel="noopener noreferrer" className="group block" data-testid={`news-card-${i}`}>{children}</a>;
                return (
                  <CardWrapper key={post.id || i}>
                    <Card className="h-full hover:shadow-xl transition-all hover:-translate-y-1 overflow-hidden">
                      {post.featured_image ? (
                        <div className="h-48 bg-slate-100 overflow-hidden">
                          <img
                            src={post.featured_image}
                            alt={post.title}
                            loading="lazy"
                            onError={(e) => { e.target.style.display = 'none'; }}
                            className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                        </div>
                      ) : (
                        <div className="h-48 bg-gradient-to-br from-sky-100 to-slate-100 flex items-center justify-center">
                          <Newspaper className="h-10 w-10 text-slate-300" />
                        </div>
                      )}
                      <CardContent className="p-5">
                        <div className="flex items-center gap-1.5 mb-2 flex-wrap">
                          {isLocal ? (
                            <Badge className="text-[10px] bg-sky-100 text-sky-700 border-sky-200 capitalize">
                              Paisley Business
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-[10px] text-slate-500">
                              via Paisley.org.uk
                            </Badge>
                          )}
                          {post.category && (
                            <Badge variant="outline" className="text-[10px] capitalize">
                              {String(post.category).replace('-', ' ')}
                            </Badge>
                          )}
                        </div>
                        <h3 className="font-heading text-base font-semibold text-slate-900 mb-2 line-clamp-2 group-hover:text-sky-600 transition-colors">
                          {post.title}
                        </h3>
                        {post.excerpt && (
                          <p className="text-sm text-slate-600 line-clamp-3 mb-3">
                            {post.excerpt}
                          </p>
                        )}
                        <div className="flex items-center justify-between text-xs text-slate-500 pt-3 border-t border-slate-100">
                          <div className="flex items-center gap-3">
                            {date && (
                              <span className="flex items-center gap-1">
                                <Calendar className="h-3 w-3" /> {date}
                              </span>
                            )}
                            {post.author && (
                              <span className="flex items-center gap-1 truncate max-w-[100px]">
                                <User className="h-3 w-3 flex-shrink-0" />
                                <span className="truncate">{post.author}</span>
                              </span>
                            )}
                          </div>
                          {isLocal
                            ? <ArrowRight className="h-3.5 w-3.5 group-hover:translate-x-0.5 transition-transform" />
                            : <ExternalLink className="h-3.5 w-3.5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                          }
                        </div>
                      </CardContent>
                    </Card>
                  </CardWrapper>
                );
              })}
            </div>
          )}

          {!loading && !errored && (
            <p className="text-center text-sm text-slate-500 mt-10" data-testid="news-attribution">
              News provided by{' '}
              <a
                href="https://paisley.org.uk"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sky-600 hover:text-sky-700 underline font-medium"
              >
                Paisley.org.uk
              </a>{' '}
              — read the full archive there.
            </p>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default NewsListPage;
